import {
  Button,
  Divider,
  Stack,
  ViewItemField,
  ViewItemFieldWithChildren,
} from '@fukuri-admins/ui/common';
import { StripePaymentField } from './StripePaymentField';
import styles from './styles.module.scss';

/**
 * 所在地3行の型定義
 */
type AddressLines = {
  /** 郵便番号（XXX-XXXX 形式、未設定の場合は '-'） */
  postalCode: string;
  /** 都道府県 + 市区町村（未設定の場合は '-'） */
  prefectureCity: string;
  /** 番地以下（未設定の場合は '-'） */
  streetAddress: string;
};

/** 会社情報セクションの表示値 */
type CompanyInfo = {
  /** 法人番号（未設定の場合は '-'） */
  corporateNumber: string;
  /** 会社名 */
  companyName: string;
  /** 会社名（フリガナ）（未設定の場合は '-'） */
  companyNameKana: string;
  /** 代表者氏名（未設定の場合は '-'） */
  representativeName: string;
  /** 所在地（3行。各行未設定の場合は '-'） */
  address: AddressLines;
  /** お取引店舗名（未設定の場合は '-'） */
  partnerBranchName: string;
};

/** プラン情報セクションの表示値 */
type PlanInfo = {
  /** 契約プラン名（有効な契約が無い場合は '-'） */
  contractPlanName: string;
  /** 利用開始日（YYYY/MM/DD 形式。有効な契約が無い場合は '-'） */
  contractStartedOnLabel: string;
};

/** ご担当者情報セクションの表示値 */
type ContactInfo = {
  /** ご担当者氏名（未設定の場合は '-'） */
  contactPersonName: string;
  /** ご担当者氏名（フリガナ）（未設定の場合は '-'） */
  contactPersonNameKana: string;
  /** メールアドレス（未設定の場合は '-'） */
  contactPersonEmail: string;
  /** 電話番号（未設定の場合は '-'） */
  contactPersonPhone: string;
};

/** ご請求先情報セクションの表示値 */
type BillingInfo = {
  /** 請求書担当者氏名（未設定の場合は '-'） */
  billingContactName: string;
  /** 請求書担当者氏名（フリガナ）（未設定の場合は '-'） */
  billingContactNameKana: string;
  /** 請求書送付先メールアドレス（未設定の場合は '-'） */
  billingEmail: string;
};

type Props = {
  /** 会社情報セクションの表示値 */
  companyInfo: CompanyInfo;
  /** プラン情報セクションの表示値 */
  planInfo: PlanInfo;
  /** ご担当者情報セクションの表示値 */
  contactInfo: ContactInfo;
  /** ご請求先情報セクションの表示値 */
  billingInfo: BillingInfo;
  /** 決済方法の表示名（「クレジットカード払い」/「請求書払い」。契約が一度もない場合は '-'） */
  settlementMethodLabel: string;
  /**
   * クレジットカード払いのご契約か。
   *
   * false のときカード情報行・編集ボタン・カード変更フォームをいずれも描画しない。
   * カード払い以外は Stripe Customer が結線されておらず、フォームを出しても
   * SetupIntent を発行できないため（編集ボタンを押せば 404 エラーになるだけ）。
   */
  isCardPayment: boolean;
  /**
   * カードブランド名（日本語） + 下4桁のフォーマット済み表示文字列（例:「マスターカード（‥‥1234）」）。
   * カード未登録（404）・カード払い以外の場合は undefined。
   */
  cardLabel?: string;
  /** カード変更フォーム（Payment Element + 保存ボタン）を展開しているか */
  isFormExpanded: boolean;
  /**
   * エラー文言（SetupIntent 発行の失敗 / カード登録の失敗）。エラーがない場合は undefined。
   *
   * フォーム内（保存ボタン付近）に表示する。SetupIntent 発行に失敗した場合は client_secret が
   * 無いため保存ボタンは非活性であり、両者のエラーが同時に起こることはない。
   */
  errorMessage?: string;
  /** カード登録成功時の案内文言。未登録・登録前は undefined */
  successMessage?: string;
  /** 保存ボタンを非活性にするか（client_secret 未取得 / SetupIntent 発行中 / 送信中） */
  isSubmitDisabled: boolean;
  /**
   * 編集ボタン押下時のハンドラ（フォームの展開 / 格納をトグルする）
   *
   * Button は `as?: 'button' | 'a'` のジェネリックなため `React.ComponentProps<typeof Button>['onClick']`
   * だと anchor 側との union になり代入できない。ここでは as を渡さず button として使うため 'button' で受ける。
   */
  onToggleForm: React.ComponentProps<'button'>['onClick'];
  /** 保存ボタン押下時のハンドラ（stripe / elements は StripePaymentField 側で取得して渡される） */
  onSubmitCard: React.ComponentProps<typeof StripePaymentField>['onSubmit'];
};

/**
 * ご契約情報 Presentation
 *
 * 会社情報 → プラン情報 → お支払い方法 → ご担当者情報 → ご請求先情報 の順に
 * セクションを Divider で区切って表示する（Figma node-id 27129-46616 の並び順）。
 *
 * 表示対象のほぼ全項目が nullable のため、未設定値は Container 側の converter で '-' に
 * 正規化済みの文字列を受け取るだけに徹する。お支払い方法セクションのみ、カード情報の表示に
 * 加えてカード変更フォーム（Payment Element）の展開 / 格納を扱う。
 *
 * カード払い以外のご契約（`isCardPayment` が false）では、決済方法の表示だけを残して
 * カード情報行・編集ボタン・カード変更フォームを描画しない。判定は converter が
 * `settlement_method` から導出した `isCardPayment` を読むだけに留める。
 */
export const ContractInfoPresentation: React.FC<Props> = ({
  companyInfo,
  planInfo,
  contactInfo,
  billingInfo,
  settlementMethodLabel,
  isCardPayment,
  cardLabel,
  isFormExpanded,
  errorMessage,
  successMessage,
  isSubmitDisabled,
  onToggleForm,
  onSubmitCard,
}) => (
  <Stack direction='column' gap={32}>
    <Stack direction='column' gap={24}>
      <h2 className={styles.title}>会社情報</h2>
      <ViewItemField label='法人番号' direction='row' value={companyInfo.corporateNumber} />
      <ViewItemField label='会社名' direction='row' value={companyInfo.companyName} />
      <ViewItemField
        label='会社名（フリガナ）'
        direction='row'
        value={companyInfo.companyNameKana}
      />
      <ViewItemField label='代表者氏名' direction='row' value={companyInfo.representativeName} />
      <ViewItemFieldWithChildren label='所在地' direction='row'>
        <Stack direction='column' gap={8}>
          <span>{companyInfo.address.postalCode}</span>
          <span>{companyInfo.address.prefectureCity}</span>
          <span>{companyInfo.address.streetAddress}</span>
        </Stack>
      </ViewItemFieldWithChildren>
      <ViewItemField label='取引店舗' direction='row' value={companyInfo.partnerBranchName} />
    </Stack>
    <Divider />
    <Stack direction='column' gap={24}>
      <h2 className={styles.title}>プラン情報</h2>
      <ViewItemField label='契約プラン' direction='row' value={planInfo.contractPlanName} />
      <ViewItemField label='利用開始日' direction='row' value={planInfo.contractStartedOnLabel} />
    </Stack>
    <Divider />
    <Stack direction='column' gap={24}>
      <h2 className={styles.title}>お支払い方法</h2>
      <ViewItemFieldWithChildren label='決済方法' direction='row'>
        <Stack direction='column' gap={8}>
          <span>{settlementMethodLabel}</span>
          {isCardPayment && (
            <Stack direction='row' gap={24} alignItems='center'>
              {cardLabel && <span>{cardLabel}</span>}
              <Button variant='secondary' size='sm' onClick={onToggleForm}>
                {isFormExpanded ? 'キャンセル' : '編集'}
              </Button>
            </Stack>
          )}
        </Stack>
      </ViewItemFieldWithChildren>
      {successMessage && <p className={styles.successMessage}>{successMessage}</p>}
      {isCardPayment && isFormExpanded && (
        <StripePaymentField
          isSubmitDisabled={isSubmitDisabled}
          errorMessage={errorMessage}
          onSubmit={onSubmitCard}
        />
      )}
    </Stack>
    <Divider />
    <Stack direction='column' gap={24}>
      <h2 className={styles.title}>ご担当者情報</h2>
      <ViewItemField label='ご担当者氏名' direction='row' value={contactInfo.contactPersonName} />
      <ViewItemField
        label='ご担当者氏名（フリガナ）'
        direction='row'
        value={contactInfo.contactPersonNameKana}
      />
      <ViewItemField
        label='メールアドレス'
        direction='row'
        value={contactInfo.contactPersonEmail}
      />
      <ViewItemField label='電話番号' direction='row' value={contactInfo.contactPersonPhone} />
    </Stack>
    <Divider />
    <Stack direction='column' gap={24}>
      <h2 className={styles.title}>ご請求先情報</h2>
      <ViewItemField
        label='請求書担当者氏名'
        direction='row'
        value={billingInfo.billingContactName}
      />
      <ViewItemField
        label='請求書担当者氏名（フリガナ）'
        direction='row'
        value={billingInfo.billingContactNameKana}
      />
      <ViewItemField
        label='請求書送付先メールアドレス'
        direction='row'
        value={billingInfo.billingEmail}
      />
    </Stack>
  </Stack>
);
